/**
 * SEO Component
 * Dynamically updates page meta tags, Open Graph, and Twitter Card tags
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogImage, 
  ogUrl,
  canonical,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
}) => {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.werkix.io';
  const currentUrl = ogUrl || `${siteUrl}${location.pathname}`;
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgImage = ogImage || `${siteUrl}/logo512.png`;
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || finalOgImage;
  const finalCanonical = canonical || currentUrl;

  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update description
    if (description) {
      updateMetaTag('description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update Open Graph tags
    if (finalOgTitle) {
      updateMetaTag('og:title', finalOgTitle, true);
    }
    if (finalOgDescription) {
      updateMetaTag('og:description', finalOgDescription, true);
    }
    if (finalOgImage) {
      updateMetaTag('og:image', finalOgImage, true);
    }
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', 'website', true);

    // Update Twitter Card tags
    updateMetaTag('twitter:card', twitterCard);
    if (finalTwitterTitle) {
      updateMetaTag('twitter:title', finalTwitterTitle);
    }
    if (finalTwitterDescription) {
      updateMetaTag('twitter:description', finalTwitterDescription);
    }
    if (finalTwitterImage) {
      updateMetaTag('twitter:image', finalTwitterImage);
    }

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', finalCanonical);

  }, [title, description, keywords, finalOgTitle, finalOgDescription, finalOgImage, currentUrl, finalCanonical, twitterCard, finalTwitterTitle, finalTwitterDescription, finalTwitterImage]);

  return null; // This component doesn't render anything
};

export default SEO;
